import React, { useState } from 'react'
import IconWithBackground from '../IconWithBackground/IconWithBackground'
import { formatCurrencyValue, formatDate } from '../../helpers/utils'
import { OrderLine, Product, ProductOption } from '../../model'
import IconInCircle from '../IconInCircle/IconInCircle'
import styles from './ProductItem.module.scss'
import DynamicFaIcon from '../DynamicFaIcon/DynamicFaIcon'

interface ProductItemProps {
    product: any
    showHighlight?: boolean
    showOptions?: boolean
    productInBasket?: undefined | Product
    disabled?: boolean
    size?: 'small' | 'medium'
    addProductToBasket?: (product: any) => void // Replace 'any' with the actual type of the product
    removeProductFromBasket?: (product: any) => void // Replace 'any' with the actual type of the product
    colors?: { iconColor?: string; iconBgColor?: string }
    orderDate?: string
    className?: string
    iconLocation?: 'center' | 'start' | 'end'
    orderPackIcon?: string
}

const ProductItem: React.FC<ProductItemProps> = ({
    product,
    showHighlight = true,
    showOptions = true,
    productInBasket,
    disabled = false,
    size = undefined,
    addProductToBasket,
    removeProductFromBasket,
    colors,
    orderDate,
    className,
    iconLocation,
    orderPackIcon
}) => {
    const [over, setOver] = useState<boolean>(false)

    const selectedProductOptions: ProductOption[] | OrderLine[] | undefined | false =
        product.options ||
        (productInBasket &&
            productInBasket.availableOptions &&
            productInBasket.availableOptions.length > 0 &&
            productInBasket?.availableOptions.filter((availableOption: any) => availableOption._isAdded))

    return (
        <div
            className={
                styles.productItem +
                ' ' +
                (productInBasket && showHighlight ? 'bg-opacity-10' : 'bg-opacity-0') +
                ' px-4 border-btm  justify-between items-center ' +
                (size === 'small' ? 'py-1' : 'py-3') +
                ' ' +
                (disabled ? 'opacity-50 pointer-events-none' : '') +
                ' ' +
                (className ? className : '')
            }
        >
            <div
                className={'flex flex-row w-full min-w-0' + (iconLocation ? ' items-' + iconLocation : ' items-center')}
            >
                <div className='flex items-center'>
                    <IconWithBackground size={size} iconName={product?.icon || 'geen'} />
                    {(product.allowInvoice3RDParty || (product.isPartOfPack && orderPackIcon)) && (
                        <IconInCircle
                            iconName={product.allowInvoice3RDParty ? 'receipt' : orderPackIcon}
                            iconPrefix={product.allowInvoice3RDParty ? 'fas' : 'fal'}
                            size='small'
                            borderColor={
                                product.allowInvoice3RDParty ? 'borderGreenColor' : 'border-[--tb-fadedPrimary]'
                            }
                            textColor={colors?.iconColor ? colors.iconColor : 'text-[--tb-green]'}
                            badge={product.allowInvoice3RDParty ? 'bottom' : 'top'}
                            tooltip={product.allowInvoice3RDParty ? 'Facturatie aan de klant mogelijk' : undefined}
                            bgColor={colors?.iconBgColor ? colors.iconBgColor : 'bg-white'}
                        />
                    )}
                </div>
                <div
                    className={
                        'flex w-full min-w-0 justify-between gap-4' +
                        (iconLocation ? ' items-' + iconLocation : ' items-center')
                    }
                >
                    <div className='flex min-w-0 ml-4 flex-col grow sm:max-w-none'>
                        <h2
                            className={
                                'text-base mb-0 justify-between w-full flex ' +
                                (size && ['small', 'medium'].includes(size) ? ' text-sm' : '')
                            }
                        >
                            <span>{product.name || product.title}</span>

                            {!orderDate && product.price !== undefined && (
                                <span className='font-normal'>{formatCurrencyValue(product.price)}</span>
                            )}
                        </h2>
                        {product.description && (
                            <span
                                className={'text-xs text-gray-500 flex-grow text-wrap ' + styles.description}
                                dangerouslySetInnerHTML={{ __html: product.description }}
                            ></span>
                        )}
                        {showOptions &&
                            selectedProductOptions &&
                            selectedProductOptions?.map((availableOption: any, i: number) => {
                                return (
                                    <div key={i} className='text-xs text-gray-500 flex justify-between'>
                                        <span>+ {availableOption.name}</span>
                                        <span>
                                            <span>{formatCurrencyValue(availableOption.price)}</span>
                                        </span>
                                    </div>
                                )
                            })}
                        {disabled && (
                            <div className='text-xs text-[--tb-primary]'>
                                <span>Al geselecteerd</span>
                            </div>
                        )}

                        {orderDate && (
                            <div className='text-sm'>
                                <strong className='mr-2 font-semibold'>Besteld:</strong>
                                <span className='text-[--tb-textLight]'>{formatDate(orderDate)}</span>
                                {product.price !== undefined && (
                                    <div className='text-xs font-semibold'>
                                        {product.price !== undefined ? formatCurrencyValue(product.price) : <>&nbsp;</>}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className='ml-4 space-x-4 flex items-center shrink-0'>
                {/* <button className='rounded-lg h-10 w-10 flex items-center justify-center border'>
                    <FontAwesomeIcon icon={faInfoCircle} />
                </button> */}
                {removeProductFromBasket && addProductToBasket && (
                    <button
                        type='button'
                        aria-label={
                            productInBasket ? 'Verwijder product uit winkelmandje' : 'Voeg product toe aan winkelmandje'
                        }
                        className='cursor-pointer'
                        onMouseOver={() => setOver(true)}
                        onMouseLeave={() => setOver(false)}
                        onClick={() =>
                            productInBasket ? removeProductFromBasket(product) : addProductToBasket(product)
                        }
                    >
                        <DynamicFaIcon
                            iconName={productInBasket ? 'check-circle' : over ? 'plus-circle' : 'plus-circle'}
                            iconPrefix={productInBasket || over ? 'fas' : 'fal'}
                            className='text-[#2EB5AD] text-3xl'
                        />
                    </button>
                )}
            </div>
        </div>
    )
}

export default ProductItem
